import { useFormik } from "formik";
import ModalComponent from "..";
import { useAdminUpdateEventFlagMutation } from "../../../redux/features/admin/event/events.api";
import { ModalCompProps } from "../../../utils/types";
import { FlagEventSchema } from "../../../utils/validations";
import { Button } from "../../buttons/button";
import Dropdown from "../../dropdown/dropdown";
import { CaretDown } from "../../icon/downcaret";
import { Icon } from "../../icon/icon";
import { TextInput } from "../../inputs/textInput";
import { MiniHeader } from "../../typography/miniHeader";

const flaggingReasons = [
  "Inappropriate Content",
  "Fake Event",
  "Violation of Terms",
  "False or Misleading Information",
  "Spam",
  "Others",
];

export const FlagEventModal = ({
  open,
  handleClose,
  eventId,
  onSuccess,
}: ModalCompProps & {
  eventId: string;
  onSuccess: () => void;
}) => {
  const [updateFlag, { isLoading }] = useAdminUpdateEventFlagMutation();

  const { values, errors, touched, handleSubmit, setFieldValue, handleChange, handleBlur, resetForm } =
    useFormik({
      initialValues: {
        reason: "",
        customReason: "",
      },

      validationSchema: FlagEventSchema,
      onSubmit: async (vals) => {
        const resp = await updateFlag({
          id: eventId,
          isFlagged: true,
          reason: vals.reason === "Others" ? vals.customReason : vals.reason,
        });

        if (resp.data?.status === "success") {
          handleClose();
          resetForm();
          onSuccess();
        }
      },
    });

  return (
    <ModalComponent
      open={open}
      handleClose={() => {
        handleClose();
        resetForm();
      }}
      className=""
      dialogClassName=""
      closeOnOverlayClick
      showHeader={false}
      title={undefined}
      subtitle={undefined}
      showBackIcon={false}
      mobileAnchorBottom
      showCloseIcon={false}
    >
      <Icon
        svg={"triangle-alert"}
        width={42}
        height={42}
        className="rounded-xl border border-grey-100 p-[11px] text-grey-700"
      />

      <div className="mb-4 mt-3">
        <MiniHeader
          header={"Flag This Event"}
          subheader={
            "Flagging this event will remove it from public view. The reason you select will be shared with the organizer."
          }
        />
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Dropdown
          menuItems={flaggingReasons.map((reason) => {
            return {
              name: reason,
              value: reason,
              onClick: () => {
                setFieldValue("reason", reason);
              },
            };
          })}
          menuClassName="!z-[999]"
          menuExactWidth
        >
          <button type="button" className="w-full text-left">
            <TextInput
              type="text"
              value={values.reason}
              hasTitle
              title="Reason for flagging"
              placeholder="Select Reason"
              onBlur={() => {}}
              onChange={() => {}}
              name="reason"
              readOnly
              errors={
                touched.reason && errors.reason ? errors.reason : undefined
              }
              customIcon={<CaretDown />}
              customIconPosition="right"
            />
          </button>
        </Dropdown>

        {values.reason === "Others" && (
          <TextInput
            type="text"
            name="customReason"
            value={values.customReason}
            onChange={handleChange}
            onBlur={handleBlur}
            hasTitle
            title="Custom reason"
            placeholder="Enter reason for flagging"
            errors={
              touched.customReason && errors.customReason
                ? errors.customReason
                : undefined
            }
          />
        )}

        <div className="flex w-full items-center justify-end gap-2">
          <Button
            text={"Cancel"}
            type="button"
            onClick={() => {
              handleClose();
              resetForm();
            }}
            variation="plain-grey"
            className="max-w-fit"
          />
          <Button
            text={"Flag Event"}
            type="submit"
            onClick={() => {}}
            className="max-w-fit"
            loading={isLoading}
            disabled={isLoading}
            variation={"danger"}
          />
        </div>
      </form>
    </ModalComponent>
  );
};

export const UnFlagEventModal = ({
  open,
  handleClose,
  eventId,
  onSuccess,
}: ModalCompProps & {
  eventId: string;
  onSuccess: () => void;
}) => {
  const [updateFlag, { isLoading }] = useAdminUpdateEventFlagMutation();
  return (
    <ModalComponent
      open={open}
      handleClose={handleClose}
      className=""
      dialogClassName=""
      closeOnOverlayClick
      showHeader={false}
      title={undefined}
      subtitle={undefined}
      showBackIcon={false}
      mobileAnchorBottom
      showCloseIcon={false}
    >
      <Icon
        svg={"triangle-alert"}
        width={42}
        height={42}
        className="rounded-xl border border-grey-100 p-[11px] text-grey-700"
      />

      <div className="mb-4 mt-3">
        <MiniHeader
          header={"Remove Flag From Event"}
          subheader={
            "Removing the flag from this event will return the event to it’s last status. It will become available to the organizer and his guests."
          }
        />
      </div>

      <div className="flex w-full items-center justify-end gap-2">
        <Button
          text={"Cancel"}
          type="button"
          onClick={() => {
            handleClose();
          }}
          variation="plain-grey"
          className="max-w-fit"
        />
        <Button
          text={"Remove Flag"}
          type="button"
          onClick={async () => {
            const resp = await updateFlag({
              id: eventId,
              isFlagged: false,
              reason: undefined,
            });

            if (resp.data?.status === "success") {
              handleClose();
              onSuccess();
            }
          }}
          className="max-w-fit"
          loading={isLoading}
          disabled={isLoading}
          variation={"primary"}
        />
      </div>
    </ModalComponent>
  );
};
